import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { AuthService } from '../../global-services/auth.service';
import { PageAttributes, PRODUCT_STATE } from '../../shared/utils';
import {
    combineLatest,
    debounceTime,
    forkJoin,
    Observable,
    Subject,
    takeUntil,
    BehaviorSubject,
} from 'rxjs';
import { DbcService } from '../../global-services/dbc.service';
import { ListResult } from '../../global-services/base-backend.service';
import { DigitalBusinessCard } from '../dbc.model';
import { concatMap, filter, finalize, map, take } from 'rxjs/operators';
import PullToRefresh from 'pulltorefreshjs';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types';
import Swiper from 'swiper';
import { User } from '../../user-account/user.model';
import { environment } from '../../../environments/environment';
import {
    AMPLITUDE_EVENT_CATEGORIES,
    AMPLITUDE_EVENTS,
    AmplitudeEvent,
    AmplitudeService,
} from '../../global-services/amplitude.service';
import { IndexedDbService } from '../../global-services/indexed-db.service';
import { OBJECT_STORES } from '../../index-db';
import { ActivatedRoute, Router } from '@angular/router';
import { getTypographyStyle, sortByLatestUpdated } from '../dbc-utils';
import { IntercomService } from '../../global-services/intercom.service';
import { DBC_SCREENS, NON_PTR_PATH_FRAGMENT } from 'app/app.constants';
import { ModalService } from 'app/global-components/modal/modal.service';
import { NudgesService, NudgeType } from '../../global-services/nudges.service';
import { FEATURE_FLAGS } from 'app/shared/feature-flags';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
    selector: 'app-dbc-list',
    templateUrl: './dbc-list.component.html',
    styleUrl: './dbc-list.component.scss',
    standalone: false
})
export class DbcListComponent implements OnDestroy, OnInit, AfterViewInit {
    @ViewChild('swiperContainer', { static: false })
    swiperContainer: ElementRef<SwiperContainer>;
    
    pageAttributes: PageAttributes = new PageAttributes({
        pageNumber: 1,
        pageSize: 5,
    });

    dbcListSubjectChange = new Subject<boolean>();
    user: User;
    isLoggedIn$: Observable<boolean>;

    isFetchingList: boolean = false;
    showSkeletonLoader: boolean = true;
    currentScreen: DBC_SCREENS = DBC_SCREENS.LIST;

    selectedDbc: DigitalBusinessCard = null;

    dbcListResult: ListResult<DigitalBusinessCard> = null;
    dbcList: Array<DigitalBusinessCard> = [];
    ngUnsubscribe: Subject<any> = new Subject();
    swiperConfig: SwiperOptions = {
        observer: true,
        observeParents: true,
        slidesPerView: 'auto',
        speed: 50,
        spaceBetween: 30,
        centeredSlides: true,
        effect: 'coverflow',
        coverflowEffect: {
            rotate: 0,
            stretch: 0,
            depth: 100,
            modifier: 1,
            slideShadows: false,
        },
        grabCursor: true,
        slideToClickedSlide: true,
    };
    DBC_SCREENS = DBC_SCREENS;
    isOpenedAsPWA: boolean = false;
    cachedUser: User;
    dbcHtmlPreviews: { [key: string]: string } = {};
    // For pull-to-refresh
    private ptrInstance: any;
    // LaunchDarkly flag value for mobile app nudge
    private isMobileAppNudgeEnabled: boolean = false;
    // Observable to track when bottom nudge component is ready
    private readonly bottomNudgeReady$ = new BehaviorSubject<boolean>(false);

    constructor(
        private authService: AuthService,
        private dbcService: DbcService,
        private amplitude: AmplitudeService,
        private indexDBService: IndexedDbService,
        private router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly intercom: IntercomService,
        private readonly modalService: ModalService,
        private readonly nudgesService: NudgesService,
        private readonly deviceDetectorService: DeviceDetectorService
    ) {
        this.isLoggedIn$ = this.authService.isLoggedIn$;
        this.authService.user$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res) => {
                if (res) {
                    this.user = res;
                }
                this.getCards();
        });

        this.indexDBService
            .getAll(OBJECT_STORES.USERS)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
                next: (value) => {
                    if (value && value.length) {
                        this.cachedUser = new User(value[0]);
                    }
                },
            });
        this.dbcListSubjectChange
            .pipe(debounceTime(100), takeUntil(this.ngUnsubscribe))
            .subscribe((res) => {
                this.fetchDbcList();
            });
    }

    getCards() {
        if (navigator.onLine) {
            this.authService.isAuthenticated$.pipe(filter((val) => val !== null)).subscribe((isAuthenticated) => {
                    if (isAuthenticated && this.user) {
                        // Authenticated and user found
                        this.fetchDbcList();
                    } else if (!isAuthenticated) {
                        this.fetchFromCache();
                    }
                });
        } else {
            this.fetchFromCache();
        }
    }

    fetchFromCache() {
        if (this.cachedUser?.isLoggedInBefore90Days) { // If last login was on (> 90 days)
            this.indexDBService.deleteDatabase().pipe(takeUntil(this.ngUnsubscribe)).subscribe({
                    next: () => {
                        this.router.navigate(['/mobile-landing']);
                    },
                    error: (err) => {
                        console.error(err);
                        this.router.navigate(['/mobile-landing']);
                    },
                });
        } else { // Login was on (< 90 days)
            this.getCardsfromCache();
        }
    }

    ngOnInit() {
        this.authService.isOpenedAsPWA$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((isPwa) => {
                this.isOpenedAsPWA = isPwa;
                if (this.isOpenedAsPWA && this.checkIfCurrentPageReloadable()) {
                    this.ptrInstance = PullToRefresh.init({
                        onRefresh() {
                            window.location.reload();
                        },
                    });
                }
            });
            
        // Wait for LaunchDarkly to be ready before checking feature flags
        this.authService.launchDarklyReady$.pipe(
            filter(ready => ready),
            take(1),
            takeUntil(this.ngUnsubscribe)
        ).subscribe(() => {
            this.fetchMobileAppNudgeFlag();
        });

        this.activatedRoute.fragment.subscribe((fragment: string) => {
            switch (fragment) {
                case DBC_SCREENS.LEAD_LIST:
                    this.switchScreen(DBC_SCREENS.LEAD_LIST);
                    break;
                case DBC_SCREENS.CAPTURE:
                    this.switchScreen(DBC_SCREENS.CAPTURE);
                    break;
                default:
                    this.switchScreen(DBC_SCREENS.LIST);
            }
        });
        this.logAmplitudeEventOnRender();

        this.modalService.primaryClick$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((modalId) => {
            if (modalId === 'editCardModal') {
                this.navigateToDashboard();
            }
        });

        this.modalService.secondaryClick$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((modalId) => {
            if (modalId === 'editCardModal') {
                this.modalService.hide();
            }
        });
    }

    openEditModal(event: Event): void {
        event.stopPropagation();
        this.modalService.show({
            heading: 'Edit Card',
            description: 'Go to the Cards dashboard to edit your digital business card.',
            primaryButtonText: 'Go to Dashboard',
            secondaryButtonText: 'Cancel',
            modalId: 'editCardModal'
        });
    }

    navigateToDashboard(): void {
        let url = environment.dashboardUrl + 'digital-business-cards/my-cards/edit/';
        url += this.selectedDbc.id.toString();
        window.open(url, '_blank');
        this.modalService.hide();
    }

    getCardsfromCache() {
        this.indexDBService.getLastAccessedOrgCards().pipe(
                takeUntil(this.ngUnsubscribe),
                finalize(() => { 
                    this.isFetchingList = false;
                    this.showSkeletonLoader = false;
                })
            )
            .subscribe((dbcList) => {
                this.dbcList = dbcList.map((dbc) => new DigitalBusinessCard(dbc)).sort(sortByLatestUpdated);
                if (this.dbcList.length > 0) {
                    this.selectedDbc = this.dbcList[0];
                    this.fetchHtmlPreviews();
                } else { // If no cards in cache and not logged in redirect to landing page
                    this.router.navigate(['/mobile-landing']);
                }
            });
    }

    ngAfterViewInit() {
        this.setUpContainerHeight();
        // Mark bottom nudge component as ready after view init
        this.bottomNudgeReady$.next(true);
    }

    setUpContainerHeight() {
        const nudgeElement = document.getElementById('install-nudge');
        
        // Use service to determine banner visibility instead of DOM detection
        const bannerToShow = this.nudgesService.determineNudgeToShow();
        const hasBottomNudge = bannerToShow === NudgeType.BOTTOM;
        const hasTopbarNudge = bannerToShow === NudgeType.TOPBAR;

        if (nudgeElement) {
            document.getElementById('list-container').style.height =
                `calc(100% - 160px)`;
        } else if (hasBottomNudge) {
            // Account for bottom nudge + bottom nav
            document.getElementById('list-container').style.height =
                `calc(100% - 200px)`;
        } else if (hasTopbarNudge) {
            // With sticky positioning, the topbar nudge naturally takes up space
            // Account for top nav + bottom nav only (topbar nudge space is handled by DOM flow)
            document.getElementById('list-container').style.height =
                `calc(100% - 162px)`; // 56px (top nav) + 106px (bottom nav)
        } else {
            document.getElementById('list-container').style.height =
                `calc(100% - 134px)`;
        }
    }

    fetchDbcList() {
        this.isFetchingList = true;

        const queryString = '&card_owner__is=' + this.user?.id;
        this.dbcService
            .getList(
                this.pageAttributes.pageNumber,
                this.pageAttributes.pageSize,
                null,
                true,
                null,
                null,
                `&state=${PRODUCT_STATE.ACTIVE}` + queryString
            )
            .pipe(
                takeUntil(this.ngUnsubscribe),
                finalize(() => {
                    this.isFetchingList = false;
                    this.showSkeletonLoader = false;
                })
            )
            .subscribe((data) => {
                this.dbcListResult = data;
                const initialLength = this.dbcList.length;

                // Append the new data to the list
                if (this.pageAttributes.pageNumber === 1) {
                    this.dbcList = [...data.objects];
                } else {
                    this.dbcList = this.dbcList.concat(...data.objects);
                }
                this.storeInIndexDB();
                this.fetchHtmlPreviews();

                // If there's no selected DBC, select the first one
                if (!this.selectedDbc && this.dbcList.length > 0) {
                    this.selectedDbc = this.dbcList[0];
                }

                // Slide to the first new card (if any new cards were added)
                if (data.objects.length > 0) {
                    setTimeout(() => {
                        const swiperInstance: Swiper =
                            this.swiperContainer.nativeElement.swiper;
                        swiperInstance.slideTo(initialLength, 0);
                    }, 50);
                }
            });
    }

    storeInIndexDB() {
        for (const dbc of this.dbcList) {
            this.indexDBService.getByID(OBJECT_STORES.CARDS, dbc.id)
                .subscribe((data) => {
                    this.updateDbcInCache(dbc);
                });
        }
    }

    updateDbcInCache(dbc: DigitalBusinessCard) {
        this.dbcService.generateDBCQrCode(dbc).then((qrCode) => {
                dbc['__qr_code_url__'] = qrCode;
            }).catch((err) => {
                console.error('Error ocurred while generating QR code', err);
            }).finally(() => {
                this.indexDBService.update(OBJECT_STORES.CARDS, dbc).subscribe(() => {});
            });
    }

    getMoreCards() {
        if (this.isFetchingList || !this.dbcListResult.next) {
            return;
        }
        this.pageAttributes.pageNumber++;
        this.dbcListSubjectChange.next(true);
    }

    logout() {
        this.authService.logout();
    }

    handleRoutingToDashboard() {
        const event = new AmplitudeEvent();
        event.eventName = AMPLITUDE_EVENTS.MYCARDS_EMPTY_STATE_CTA_CLICKED;
        this.logAmplitudeEvent(AMPLITUDE_EVENT_CATEGORIES.Usage, event);
        window.open(environment.redirectToDashboardCardList, '_blank');
    }

    noDbcExists(): boolean {
        return this.dbcList.length === 0;
    }

    logAmplitudeEvent(
        type: AMPLITUDE_EVENT_CATEGORIES,
        event: AmplitudeEvent
    ): void {
        this.amplitude.logEvent(type, event.eventName, event.properties);
    }

    onSlideChange(e) {
        const activeIndex = e.target.swiper.activeIndex;
        if (activeIndex === this.dbcList?.length) {
            /*
       Resetting selectedDbc to null when the user scrolls
       to the end of the list and enters the "load more" screen,
       ensuring no card remains selected. Also bottom button visibility
       is controlled by the selectedDbc variable.
      * */
            this.selectedDbcChange(null);
        }
        this.dbcList.filter((dbc, index) => {
            if (index === activeIndex) {
                this.selectedDbcChange(dbc);
            }
        });
    }

    selectedDbcChange(dbc: DigitalBusinessCard) {
        this.selectedDbc = dbc;
    }

    switchScreen(screen: DBC_SCREENS) {
        this.currentScreen = screen;
    }

    onShareButtonClicked() {
        if (this.selectedDbc) {
            this.switchScreen(DBC_SCREENS.SHARE);
        }
    }

    onImageCaptureClicked() {
        this.switchScreenUsingFragment(DBC_SCREENS.CAPTURE);
    }

    onLeadListViewClicked() {
        this.switchScreenUsingFragment(DBC_SCREENS.LEAD_LIST);
    }

    logAmplitudeEventOnRender() {
        const event = new AmplitudeEvent();
        event.eventName = AMPLITUDE_EVENTS.MYCARDS_HOME_PAGE_OPENED;
        event.properties = {
            opened_as: this.amplitude.setAmplitudeEventProperties(),
            device_type: this.amplitude.setDeviceProperties(),
        };

        this.logAmplitudeEvent(AMPLITUDE_EVENT_CATEGORIES.Engagement, event);
    }

    fetchHtmlPreviews() {
        const previewRequests = this.dbcList.map((dbc) =>
            this.dbcService.getPreviewHtml(dbc.__preview_mobile_url__).pipe(
                takeUntil(this.ngUnsubscribe),
                map((res) => ({ dbc, res }))
            )
        );

        forkJoin(previewRequests).pipe(
            concatMap((previews) => {
                    const shadowDomObservables = previews.map(({ dbc, res }) =>
                        this.attachPreviewShadowDom$(dbc.id.toString(),res,dbc)
                    );

                    return combineLatest(shadowDomObservables);
                }), finalize(() => {
                    this.onAllPreviewsFetched();
                })
            ).subscribe({
                next: () => {},
                error: (err) => console.error('Error processing previews', err),
            });
    }

    onAllPreviewsFetched() {
        if (navigator.onLine) {
            this.intercom.loadIntercomScript();
        } else {
            console.log("User is offline, skipping Intercom script load.");
        }
        this.setUpContainerHeight();
    }

    appendStylesToRootTag(card: DigitalBusinessCard, typographyStyles: string) {
        // Create a <style> element for the @font-face and other styles
        const style = document.createElement('style');
        style.textContent = typographyStyles;

        // Append the style element to the <head> of the document
        document.head.appendChild(style);

        if (card.customizations.typography.font_type === 'google') {
            // In case of google font type, add a <link> element to the <head> of the document to load the font
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = `https://fonts.googleapis.com/css?family=${card.customizations.typography.font_family}:wght@100;300;400;500;600;700&display=swap`;
            document.head.appendChild(link);
        }
    }

    // Function to attach DBC preview to Shadow DOM
    attachPreviewShadowDom$(id: string,html: string,card: DigitalBusinessCard): Observable<void> {
        return new Observable((observer) => {
            const typographyStyles = getTypographyStyle(card);
            this.appendStylesToRootTag(card, typographyStyles);

            setTimeout(() => {
                const container = document.getElementById(id);
                if (container && !container.shadowRoot) {
                    const shadowRoot = container.attachShadow({ mode: "open" });
                    shadowRoot.innerHTML = html;
                }
                observer.next();
                observer.complete();
            }, 100);
        });
    }

    onShareCancelled() {
        this.switchScreen(DBC_SCREENS.SHARE);
    }

    // Check if the current page is reloadable
    private checkIfCurrentPageReloadable() {
        let isReloadable = true;
        this.activatedRoute.fragment.subscribe((route) => {
            isReloadable = NON_PTR_PATH_FRAGMENT.includes(route) ? false : true;
        });
        return isReloadable;
    }

    // Fragment navigation
    private switchScreenUsingFragment(screen: DBC_SCREENS) {
        this.router.navigate([''], {
            fragment: screen,
            skipLocationChange: false,
        });
    }

    private fetchMobileAppNudgeFlag(): void {
        this.authService.getFeatureFlag(FEATURE_FLAGS.DBC_MOBILE_APP_NUDGE, false).then(flag => {
            this.isMobileAppNudgeEnabled = flag;
            this.showMobileNudge();
        })
    }

    showMobileNudge(): void {
        if (!this.isMobileAppNudgeEnabled) {
            return;
        }

        // Check if device supports app store links
        if (!this.deviceDetectorService.isMobile()) {
            return;
        }

        // Check which banner should be shown based on service logic
        const bannerToShow = this.nudgesService.determineNudgeToShow();
        
        if (bannerToShow === NudgeType.BOTTOM) {
            // Use RxJS observable to ensure component is ready before showing nudge
            this.bottomNudgeReady$.pipe(filter(ready => ready), take(1), takeUntil(this.ngUnsubscribe)).subscribe(() => {
                // Call the service directly instead of the component
                this.nudgesService.showBottomNudge();
            });
        } else if (bannerToShow === NudgeType.TOPBAR) {
            // Call the service directly instead of the wrapper method
            this.nudgesService.showTopbarNudge();
        }
        // If bannerToShow is NudgeType.NONE, don't show any banner
    }

    onBottomNudgeClosed(): void {
        // Update container height when bottom nudge is closed
        this.setUpContainerHeight();
        
        // Check if topbar nudge should be shown
        if (this.nudgesService.shouldShowTopbarNudge()) {
            this.nudgesService.showTopbarNudge();
        }
    }

    ngOnDestroy() {
        this.intercom.removeIntercomScript();
        this.ngUnsubscribe.next(null);
        this.ngUnsubscribe.complete();
        if (this.ptrInstance) {
            this.ptrInstance.destroy();
        }
    }
}
